import React from "react";
import logger from "../lib/logger";

/**
 * @typedef {object} ErrorBoundaryProps
 * @property {React.ReactNode} children - React subtree protected by the boundary.
 */

/**
 * @typedef {object} ErrorBoundaryState
 * @property {boolean} hasError - Whether a render error has been captured.
 */

/**
 * Catches render-time React errors so a broken widget does not blank the app.
 * @extends {React.Component<ErrorBoundaryProps, ErrorBoundaryState>}
 */
export default class ErrorBoundary extends React.Component {
  /**
   * @param {ErrorBoundaryProps} props - Boundary props.
   */
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * Switch the boundary into fallback UI mode after a child render failure.
   * @returns {ErrorBoundaryState}
   */
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  /**
   * Log the component stack for render-time errors.
   * @param {Error} error - Captured React error.
   * @param {React.ErrorInfo} info - Component stack metadata.
   * @returns {void}
   */
  componentDidCatch(error, info) {
    logger.error("react.error_boundary", {
      message: error?.message,
      stack: error?.stack,
      componentStack: info?.componentStack,
    });
  }

  /**
   * Render children or a recovery screen after an error.
   * @returns {React.ReactNode}
   */
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen app-page flex items-center justify-center px-6">
          <div className="max-w-md w-full bg-surface border border-earth rounded-2xl p-8 text-center shadow-lg">
            <h1 className="font-serif-display text-3xl text-ink mb-3">
              Something went wrong
            </h1>
            <p className="text-ink-soft mb-6">
              We logged the issue. Please refresh the page and try again.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-pill btn-primary"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
